import { CommonModule } from '@angular/common';
import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { AuthService } from '../../core/auth/auth.service';
import { LayoutService } from '../../core/layout/layout.service';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { environment } from '../../../environments/environment';
import { Title } from '@angular/platform-browser';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { Subject, takeUntil } from 'rxjs';

@Component({
  selector: 'app-sign-in',
  standalone: true,
  imports: [CommonModule, TranslocoModule, RouterModule, FormsModule, ReactiveFormsModule],
  templateUrl: './sign-in.component.html',
  styleUrl: './sign-in.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SignInComponent implements OnInit, AfterViewInit, OnDestroy {
  public errorMessage: string = '';

  public loginGroup: FormGroup = new FormGroup({
    email: new FormControl('', [Validators.required, Validators.email]),
    password: new FormControl('', [Validators.required, Validators.minLength(8)]),
  });

  private _unsubscribeAll: Subject<any> = new Subject<any>();

  constructor(
    private authService: AuthService,
    private layoutService: LayoutService,
    private cd: ChangeDetectorRef,
    private route: ActivatedRoute,
    private router: Router,
    private title: Title,
    private transloco: TranslocoService,
    private elementRef: ElementRef
  ) {
    this.route.queryParams;
  }

  // -----------------------------------------------------------------------------------------------------
  //  @ Lifecycle Hooks
  // -----------------------------------------------------------------------------------------------------
  ngOnInit(): void {
    this.title.setTitle(this.transloco.translate('Sign In') + ' - zammy');
    this.authService.initGoogleOnetap();
  }

  ngAfterViewInit(): void {}

  ngOnDestroy(): void {
    this._unsubscribeAll.next(null);
    this._unsubscribeAll.complete();
    this._unsubscribeAll.unsubscribe();
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Public methods
  // -----------------------------------------------------------------------------------------------------

  public submit(): void {
    this.layoutService.loading = true;
    this.authService
      .signIn(this.loginGroup.value.email, this.loginGroup.value.password)
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe((res: any) => {
        if (res.data.tokenCreate.errors.length > 0) {
          if (this.errorMessage === `Account needs to be confirmed via email.`) {
            this.layoutService.loading = false;
            this.router.navigate(['/verification'], { queryParams: { redirectUrl: this.authService.redirectUrl, email: this.loginGroup.value.email } });
          }

          this.errorMessage = res.data.tokenCreate.errors[0].message;
          this.layoutService.loading = false;
          this.cd.markForCheck();
          return;
        }

        if (!res.data.tokenCreate.token || !res.data.tokenCreate.refreshToken) {
          this.errorMessage = 'Invalid credentials.';
          this.layoutService.loading = false;
          this.cd.markForCheck();
          return;
        }

        this.authService.accessToken = res.data.tokenCreate.token;
        this.authService.refreshToken = res.data.tokenCreate.refreshToken;

        window.location.href = this.authService.redirectUrl;
      });
  }

  public onClickSignUp(): void {
    this.router.navigate(['/sign-up'], { queryParams: { redirectUrl: this.authService.redirectUrl } });
  }

  public onClickForgotPassword(): void {
    this.router.navigate(['/forgot-password'], { queryParams: { redirectUrl: this.authService.redirectUrl } });
  }

  public onClickGoogleLogin(): void {
    window.location.href = `https://accounts.google.com/o/oauth2/v2/auth?response_type=code&client_id=${environment.google.clientId}&redirect_uri=${environment.domain}/google&scope=openid%20email%20profile`;
    // window.location.href = `https://accounts.google.com/o/oauth2/auth?client_id=${environment.google.clientId}&redirect_uri=${environment.domain}/auth/google&response_type=code&scope=https://www.googleapis.com/auth/analytics`;
  }
}
